import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, Check } from 'lucide-react';
import type { TaskSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const myTasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-tasks',
  component: MyTasksPage,
});

function MyTasksPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);

  const inboxQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.getMyTasks(),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // "Done" is whichever column the task's own project marks as finished work —
  // board columns are per-project, so the target is looked up, never assumed.
  const completeMutation = useMutation({
    mutationFn: async (task: TaskSummary) => {
      const statuses = await api.listTaskStatuses(task.projectId);
      const finished = statuses
        .filter((s) => s.kind === 'finished' && !s.archived)
        .sort((a, b) => a.position - b.position)[0];
      if (!finished)
        throw new Error('This project has no "finished" column yet — add one on the board.');
      await api.updateTask(task.id, { statusId: finished.id });
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const inbox = inboxQuery.data;
  const overdue = inbox?.overdue ?? [];
  const today = inbox?.today ?? [];
  const upcoming = inbox?.upcoming ?? [];
  const later = inbox?.later ?? [];
  const completed = inbox?.completed ?? [];

  const sectionProps = {
    onComplete: (t: TaskSummary) => completeMutation.mutate(t),
    completingId: completeMutation.isPending ? (completeMutation.variables?.id ?? null) : null,
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">My tasks</h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Buckets recompute at IST midnight. Mark a task done here, or open its board to move it
          between columns.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not update task</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Section
        title="Overdue"
        badge={overdue.length > 0 ? <Badge variant="destructive">{overdue.length}</Badge> : null}
        tasks={overdue}
        {...sectionProps}
      />
      <Section
        title="Today"
        badge={<Badge variant="muted">{today.length}</Badge>}
        tasks={today}
        {...sectionProps}
      />
      <Section
        title="Upcoming (next 7 days)"
        badge={<Badge variant="muted">{upcoming.length}</Badge>}
        tasks={upcoming}
        {...sectionProps}
      />
      <Section
        title="Later / no due date"
        badge={<Badge variant="muted">{later.length}</Badge>}
        tasks={later}
        {...sectionProps}
      />
      <Section
        title="Completed"
        badge={<Badge variant="muted">{completed.length}</Badge>}
        tasks={completed}
        {...sectionProps}
      />
    </AppShell>
  );
}

function Section({
  title,
  badge,
  tasks,
  onComplete,
  completingId,
}: {
  title: string;
  badge: React.ReactNode;
  tasks: TaskSummary[];
  onComplete: (task: TaskSummary) => void;
  completingId: string | null;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          {title} {badge}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {tasks.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Nothing here.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
            {tasks.map((t) => (
              <li
                key={t.id}
                className="flex flex-col gap-2 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: t.projectId }}
                  className="group flex min-w-0 flex-1 flex-col gap-0.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                >
                  <span className="flex items-center gap-1 font-medium group-hover:underline">
                    {t.title}
                    <ArrowUpRight className="size-3.5 text-[hsl(var(--muted-foreground))] opacity-0 transition-opacity group-hover:opacity-100" />
                  </span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">
                    <span className="whitespace-nowrap">
                      {t.dueOn ? `Due ${t.dueOn}` : 'No due date'}
                    </span>{' '}
                    · open board
                  </span>
                </Link>
                <div className="flex shrink-0 items-center justify-between gap-3">
                  <Badge variant={t.statusKind === 'finished' ? 'default' : 'muted'}>
                    {t.statusLabel}
                  </Badge>
                  {t.statusKind !== 'finished' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={completingId === t.id}
                      onClick={() => onComplete(t)}
                      title="Move to this project's finished column"
                    >
                      <Check className="size-4" />
                      {completingId === t.id ? 'Saving…' : 'Mark done'}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
